import { api } from './api'
import { getAll } from './pagination'

// Enhanced Timetable Service with comprehensive functionality
export interface TimetablePeriod {
  id: number
  name: string
  start_time: string
  end_time: string
  /** A lesson period, or time every class shares: break, lunch, sports. */
  kind: 'teaching' | 'break' | 'lunch' | 'sports'
  /** True for every kind but a lesson; kept for older screens. */
  is_break: boolean
  /** Class levels not at school during it, e.g. the nursery after 1 pm. */
  excluded_levels: string[]
  order: number
  academic_year: number
  academic_year_name: string
  duration_minutes?: number
}

export interface TimetableSlot {
  id: number
  class_assigned: number
  class_name: string
  subject: number
  subject_name: string
  teacher: number
  teacher_name: string
  period: number
  period_name: string
  class_level?: string
  day_of_week: number
  day_name: string
  room: string | null
  academic_year: number
  academic_year_name: string
  is_active: boolean
  is_conflicted?: boolean
  conflict_reason?: string
}

export interface TimetableGeneration {
  id: number
  name: string
  status: 'pending' | 'generating' | 'completed' | 'incomplete' | 'failed' | 'optimizing'
  conflicts_found: number
  warnings_found: number
  /** What the generator did, a line per step. */
  generation_log?: string
  generation_time: string | null
  is_active: boolean
  created_at: string
  generated_by: number
  generated_by_name: string
  academic_year: number
  academic_year_name: string
  template?: number | null
  template_name?: string | null
}

export interface TimetableConflict {
  id: number
  conflict_type: 'teacher_double_booking' | 'room_double_booking' | 'class_double_booking' | 'teacher_unavailable' | 'room_unavailable' | 'constraint_violation'
  description: string
  severity: 'low' | 'medium' | 'high' | 'critical'
  is_resolved: boolean
  affected_slots: number[]
  suggested_solutions?: string[]
}

export interface TimetableConstraint {
  id: number
  name: string
  constraint_type: 'teacher_availability' | 'room_availability' | 'subject_preference' | 'class_preference' | 'break_requirement' | 'double_period' | 'no_double_booking'
  description: string
  is_active: boolean
  is_hard_constraint: boolean
  weight: number
  parameters: Record<string, any>
  academic_year: number
}

export interface TimetableTemplate {
  id: number
  name: string
  description: string
  is_default: boolean
  academic_year: number
  template_data: Record<string, any>
  created_at: string
}

export interface TimetableRoom {
  id: number
  name: string
  capacity: number | null
  room_type: 'classroom' | 'laboratory' | 'library' | 'hall' | 'office' | 'special'
  equipment: string[]
  is_active: boolean
  availability: {
    day_of_week: number
    periods: number[]
  }[]
}

export interface TimetableRequest {
  id: number
  requested_by: number
  requested_by_name: string
  request_type: 'change_teacher' | 'change_time' | 'change_room' | 'add_slot' | 'remove_slot' | 'swap_slots'
  current_slot: number
  current_slot_display: string
  requested_changes: Record<string, unknown>
  reason: string
  status: 'pending' | 'approved' | 'rejected' | 'implemented'
  reviewed_by: number | null
  reviewed_by_name: string
  review_notes: string
  academic_year: number
  academic_year_name: string
  created_at: string
}

export interface TimetableAnalytics {
  utilization_stats: {
    teacher_utilization: number
    room_utilization: number
    class_utilization: number
  }
  conflict_summary: {
    total_conflicts: number
    resolved_conflicts: number
    critical_conflicts: number
  }
  workload_distribution: {
    teacher_id: number
    teacher_name: string
    total_periods: number
    workload_percentage: number
  }[]
  room_usage: {
    room_id: number
    room_name: string
    usage_percentage: number
    peak_hours: string[]
  }[]
}

class TimetableService {
  // Period Management
  async getPeriods(academicYearId?: number): Promise<TimetablePeriod[]> {
    const params = academicYearId ? `?academic_year_id=${academicYearId}` : ''
    return getAll(`timetable/periods/${params}`)
  }

  async createPeriod(data: Partial<TimetablePeriod>): Promise<TimetablePeriod> {
    const response = await api.post('timetable/periods/', data)
    return response.data
  }

  /** With `in_use`: the lessons (and requests) deleting it would take. */
  async getPeriod(id: number): Promise<TimetablePeriod & { in_use: Record<string, number> }> {
    return (await api.get(`timetable/periods/${id}/`)).data
  }

  /** Lays out the whole day at once, keeping lessons with their periods. */
  async setSchoolDay(academicYear: number, periods: Array<Pick<TimetablePeriod, 'name' | 'kind' | 'start_time' | 'end_time' | 'excluded_levels'>>): Promise<{
    periods: TimetablePeriod[]
    stranded_lessons: number
    stranded_classes: string[]
  }> {
    return (await api.post('timetable/periods/school-day/', { academic_year: academicYear, periods })).data
  }

  async updatePeriod(id: number, data: Partial<TimetablePeriod>): Promise<TimetablePeriod> {
    const response = await api.patch(`timetable/periods/${id}/`, data)
    return response.data
  }

  async deletePeriod(id: number): Promise<void> {
    await api.delete(`timetable/periods/${id}/`)
  }

  // Slot Management
  async getSlots(filters?: {
    class_id?: number
    teacher_id?: number
    day_of_week?: number
    academic_year_id?: number
  }): Promise<TimetableSlot[]> {
    const params = new URLSearchParams()
    if (filters?.class_id) params.append('class_id', filters.class_id.toString())
    if (filters?.teacher_id) params.append('teacher_id', filters.teacher_id.toString())
    if (filters?.day_of_week !== undefined) params.append('day_of_week', filters.day_of_week.toString())
    if (filters?.academic_year_id) params.append('academic_year_id', filters.academic_year_id.toString())
    
    return getAll(`timetable/slots/?${params.toString()}`)
  }

  async createSlot(data: Partial<TimetableSlot>): Promise<TimetableSlot> {
    const response = await api.post('timetable/slots/', data)
    return response.data
  }

  async updateSlot(id: number, data: Partial<TimetableSlot>): Promise<TimetableSlot> {
    const response = await api.put(`timetable/slots/${id}/`, data)
    return response.data
  }

  async deleteSlot(id: number): Promise<void> {
    await api.delete(`timetable/slots/${id}/`)
  }

  // Drag and Drop Operations
  async moveSlot(slotId: number, newData: {
    day_of_week?: number
    period_id?: number
    room?: string
  }): Promise<TimetableSlot> {
    const response = await api.patch(`timetable/slots/${slotId}/move/`, newData)
    return response.data
  }

  async swapSlots(slot1Id: number, slot2Id: number): Promise<{
    slot1: TimetableSlot
    slot2: TimetableSlot
  }> {
    const response = await api.post('timetable/swap-slots/', {
      slot1_id: slot1Id,
      slot2_id: slot2Id
    })
    return response.data
  }

  // Generation Management
  async getGenerations(): Promise<TimetableGeneration[]> {
    return getAll('timetable/generations/')
  }

  async generateTimetable(data: {
    name: string
    academic_year_id: number
    template_id?: number
    force_regenerate?: boolean
    constraints?: number[]
  }): Promise<TimetableGeneration> {
    const response = await api.post('timetable/generate/', data)
    return response.data
  }

  async optimizeTimetable(generationId: number, options?: {
    focus_on?: 'conflicts' | 'workload' | 'room_usage'
    max_iterations?: number
  }): Promise<{ message: string; improvements: Record<string, number> }> {
    const typeMap = {
      conflicts: 'conflict_resolution',
      workload: 'teacher_preference',
      room_usage: 'room_optimization'
    } as const
    const response = await api.post(`timetable/optimize/`, {
      generation_id: generationId,
      max_iterations: options?.max_iterations,
      optimization_type: options?.focus_on ? typeMap[options.focus_on] : 'general_optimization'
    })
    return response.data
  }

  // Conflict Management
  async getConflicts(generationId: number): Promise<TimetableConflict[]> {
    return getAll(`timetable/generations/${generationId}/conflicts/`)
  }

  // Room Management
  async getRooms(): Promise<TimetableRoom[]> {
    return getAll('timetable/rooms/')
  }

  // Constraint Management
  async getConstraints(): Promise<TimetableConstraint[]> {
    return getAll('timetable/constraints/')
  }

  async createConstraint(data: Partial<TimetableConstraint>): Promise<TimetableConstraint> {
    const response = await api.post('timetable/constraints/', data)
    return response.data
  }

  // Template Management
  async getTemplates(): Promise<TimetableTemplate[]> {
    return getAll('timetable/templates/')
  }

  async createTemplate(data: Partial<TimetableTemplate>): Promise<TimetableTemplate> {
    const response = await api.post('timetable/templates/', data)
    return response.data
  }

  // Analytics
  async getAnalytics(academicYearId?: number): Promise<TimetableAnalytics> {
    const params = academicYearId ? `?academic_year_id=${academicYearId}` : ''
    const response = await api.get(`timetable/analytics/${params}`)
    return response.data
  }

  async getStatistics(academicYearId?: number): Promise<{
    total_slots: number
    total_periods: number
    total_classes: number
    total_teachers: number
    unresolved_conflicts: number
    active_generation: TimetableGeneration | null
  }> {
    const response = await api.get('timetable/statistics/', {
      params: academicYearId ? { academic_year_id: academicYearId } : undefined
    })
    return response.data
  }

  // Individual Views
  async getClassTimetable(classId: number, academicYearId?: number): Promise<TimetableSlot[]> {
    const params = academicYearId ? `?academic_year_id=${academicYearId}` : ''
    const response = await api.get(`timetable/classes/${classId}/timetable/${params}`)
    return response.data
  }

  async getTeacherTimetable(teacherId: number, academicYearId?: number): Promise<TimetableSlot[]> {
    const params = academicYearId ? `?academic_year_id=${academicYearId}` : ''
    const response = await api.get(`timetable/teachers/${teacherId}/timetable/${params}`)
    return response.data
  }

  // Bulk Operations
  async bulkUpdateSlots(updates: Array<{
    id: number
    data: Partial<TimetableSlot>
  }>): Promise<TimetableSlot[]> {
    const response = await api.post('timetable/slots/bulk-update/', { updates })
    return response.data
  }

  async bulkDeleteSlots(slotIds: number[]): Promise<void> {
    await api.post('timetable/slots/bulk-delete/', { slot_ids: slotIds })
  }

  // Export/Import
  async exportTimetable(filters?: {
    class_id?: number
    teacher_id?: number
    academic_year_id?: number
  }): Promise<Blob> {
    const params = new URLSearchParams()
    if (filters?.class_id) params.append('class_id', filters.class_id.toString())
    if (filters?.teacher_id) params.append('teacher_id', filters.teacher_id.toString())
    if (filters?.academic_year_id) params.append('academic_year_id', filters.academic_year_id.toString())
    
    const response = await api.get(`timetable/export/?${params.toString()}`, {
      responseType: 'blob'
    })
    return response.data
  }

  async downloadTeacherTimetablePdf(teacherId: number): Promise<Blob> {
    const response = await api.get(`timetable/teachers/${teacherId}/timetable/?export=pdf`, {
      responseType: 'blob'
    })
    return response.data
  }

  async downloadClassTimetablePdf(classId: number): Promise<Blob> {
    const response = await api.get(`timetable/classes/${classId}/timetable/?export=pdf`, {
      responseType: 'blob'
    })
    return response.data
  }

  async importTimetable(file: File, options?: {
    overwrite_existing?: boolean
    validate_only?: boolean
    academic_year_id?: number
  }): Promise<{
    imported_count: number
    validated_count: number
    updated_count: number
    errors: string[]
    warnings: string[]
  }> {
    const formData = new FormData()
    formData.append('file', file)
    if (options?.overwrite_existing) formData.append('overwrite_existing', 'true')
    if (options?.validate_only) formData.append('validate_only', 'true')
    if (options?.academic_year_id) formData.append('academic_year_id', String(options.academic_year_id))
    
    const response = await api.post('timetable/import/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    return response.data
  }

  async getRequests(status?: TimetableRequest['status']): Promise<TimetableRequest[]> {
    return getAll('timetable/requests/', status ? { status } : undefined)
  }

  async createRequest(data: {
    request_type: TimetableRequest['request_type']
    current_slot: number
    requested_changes: Record<string, unknown>
    reason: string
  }): Promise<TimetableRequest> {
    return (await api.post('timetable/requests/', data)).data
  }

  async reviewRequest(id: number, decision: 'approved' | 'rejected', reviewNotes: string): Promise<TimetableRequest> {
    return (await api.post(`timetable/requests/${id}/review/`, {
      decision, review_notes: reviewNotes
    })).data
  }

  async implementRequest(id: number): Promise<{ request: TimetableRequest; slot: TimetableSlot }> {
    return (await api.post(`timetable/requests/${id}/implement/`, {})).data
  }
}

export const timetableService = new TimetableService()
